import React from "react";
import { reportClientError } from "../../core/errorReporter.js";
import "./list-item-boundary.scss";

/**
 * Per-item error boundary for listing grids.
 *
 * Without it, ONE listing whose card throws (an unexpected null in its data, a
 * bad marketplace payload) takes the whole page down: the app-level
 * ErrorBoundary catches the throw, retries the subtree in place, and that
 * remount resets every filter the user had just set — the grid goes blank, the
 * pills snap back off, and the "N Listings" count is the only thing left
 * standing, because it was set from the same response the cards choked on.
 *
 * Here the failure stays inside the one tile, names the listing and the
 * error, and is reported the same way the app-level boundary reports — so the
 * message is on screen for whoever is looking AND in client_error_events for
 * whoever is fixing it.
 */
class ListItemBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    reportClientError({
      type: "listing_card_render_error",
      message: (error && error.message) || "Listing card failed to render",
      context: {
        listingId: this.props.listingId ?? null,
        stack: (error && error.stack) || null,
        componentStack: (errorInfo && errorInfo.componentStack) || null,
      },
    });
  }

  render() {
    if (!this.state.error) return this.props.children;

    const { listingId, reference } = this.props;
    const label = [reference, listingId != null ? `#${listingId}` : null].filter(Boolean).join(" · ");
    const message = String((this.state.error && this.state.error.message) || this.state.error);

    return (
      <div className="list-item-error" role="alert">
        <span className="list-item-error__title">This listing couldn’t be displayed</span>
        {label && <span className="list-item-error__meta">{label}</span>}
        <code className="list-item-error__msg">{message}</code>
      </div>
    );
  }
}

export default ListItemBoundary;
